import { useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { ProjectSchemeInterface } from 'ts-interfaces';

export const useProjectSelection = (ownedProjects: ProjectSchemeInterface[], sharedProjects: ProjectSchemeInterface[], selectedProjectId?: string) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [showMoreOwned, setShowMoreOwned] = useState(false);
  const [showMoreShared, setShowMoreShared] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter projects based on search term
  const filterProjects = (projects: ProjectSchemeInterface[]) => {
    if (!searchTerm.trim()) return projects;
    return projects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredOwnedProjects = filterProjects(ownedProjects);
  const filteredSharedProjects = filterProjects(sharedProjects);

  // Get limited projects for display
  const getDisplayedProjects = (projects, showMore) => {
    let displayedProjects = showMore ? projects : projects.slice(0, 4);
    
    // Always include selected project if not already displayed
    if (selectedProjectId) {
      const selectedProject = projects.find(p => p.id === selectedProjectId);
      if (selectedProject && !displayedProjects.some(p => p.id === selectedProjectId)) {
        displayedProjects = [selectedProject, ...displayedProjects.slice(0, 3)];
      }
    }
    
    return displayedProjects;
};

  const displayedOwnedProjects = getDisplayedProjects(filteredOwnedProjects, showMoreOwned);
  const displayedSharedProjects = getDisplayedProjects(filteredSharedProjects, showMoreShared);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleNavigateToProject = (id: string) => {
    navigate(`/projects/show/${id}`);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
    setSearchTerm('');
    setHoveredProject(null);
    setShowMoreOwned(false);
    setShowMoreShared(false);
  };

  return {
    // State
    searchTerm,
    dropdownOpen,
    hoveredProject,
    showMoreOwned,
    showMoreShared,
    searchInputRef,
    
    // Filtered data
    filteredOwnedProjects,
    filteredSharedProjects,
    displayedOwnedProjects,
    displayedSharedProjects,
    
    // Actions
    setDropdownOpen,
    setHoveredProject,
    setShowMoreOwned,
    setShowMoreShared,
    handleSearchChange,
    clearSearch,
    handleNavigateToProject,
    handleDropdownClose
  };
};